import React, {useCallback, useMemo, useState} from 'react';
import {
  Grid,
  Tooltip,
  Card,
  CardContent,
  Box,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {EventData} from '@/types/event';
import {Done, Repeat} from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import {formatDate, getCompanyLabel} from '@/utils/';
import {getDistanceFromLatLonInKm} from '@/utils';
import {useTranslation} from 'react-i18next';
import {useAppSelector} from '@/redux-store/store';
import {CardMediaComponent} from '@/components/ui/card-media';
import LockIcon from '@mui/icons-material/Lock';
import {env} from '@/config';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import EuroIcon from '@mui/icons-material/Euro';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import {isEventInPast, isUserEventParticipant} from '@/services/events';

interface EventCardProps {
  event: EventData;
  onEventCardClick: (event: EventData) => void;
  fullWidth?: boolean;
  cardMediaHeight?: string;
  cardMediaTypography?: 'h4' | 'h6';
  customTitleWidth?: string;
  alwaysTruncate?: boolean;
  smallerVariant?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onEventCardClick,
  fullWidth = false,
  cardMediaHeight,
  cardMediaTypography,
  smallerVariant = false
}) => {
  const {t, i18n} = useTranslation();

  const {CONTENT_SERVER, APP_VARIANT} = env;

  const theme = useTheme();
  const isXsUp = useMediaQuery(theme.breakpoints.up('xs'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const {unreadEventsCount} = useMatrixUnreadEventsContext();

  const userCompanies = useAppSelector(
    (state) => state.userInfos.userInfos.companies
  );

  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);

  const isUserTrainer =
    userRole && userRole === 'TRAINER' && APP_VARIANT === 'sjone';

  const isSmScreen = isXsUp && isMdDown;

  const [showMainTooltip, setShowMainTooltip] = useState(false);

  const userInfos = useAppSelector((state) => state.userInfos.userInfos);

  const {sports} = useAppSelector((state) => state.sports);

  const eventFilter = useAppSelector((state) => state.filter['event']);

  const handleOtherTooltipMouseEnter = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  const handleOtherTooltipMouseLeave = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  const isUserParticipant = isUserEventParticipant(event, userInfos._id);

  const {isPaid, trainerEvent} = event;

  const chipSize = smallerVariant ? 'small' : 'medium';

  const eventCancelled = event.status === 'CANCELLED';

  const pastEvent = isEventInPast(event);

  const sportId = event.sports[0]?.sport;
  const sport = typeof sportId === 'string' ? sports[sportId] : sportId;

  const sportLabel =
    i18n.language === 'en' ? sport?.labels.en : sport?.labels.de;

  const imageUrl = (sport?.picture || '').startsWith('http')
    ? sport?.picture
    : `${CONTENT_SERVER}${sport?.picture || ''}`;

  const maxParticipants = event.maxParticipants;

  const getParticipantsLabel = useCallback(() => {
    const currentParticipants = event.participants.length;
    let label = currentParticipants.toString();
    if (maxParticipants) {
      if (
        maxParticipants < 5 ||
        (maxParticipants >= 5 && currentParticipants >= 3)
      ) {
        label = `${currentParticipants} / ${maxParticipants}`;
      }
    }
    return label;
  }, [event.participants.length, maxParticipants]);

  const formattedDate = formatDate(
    new Date(event.start),
    event.recurring,
    smallerVariant
  );

  const responsiveTypographyTitleProps = smallerVariant ? {} : {medium: true};

  const responsiveTypographyDateProps = smallerVariant
    ? {mediumAllSizes: true}
    : {mediumSmall: true};

  const eventDistance = useMemo(() => {
    let distance: number;
    const filterCoords = eventFilter.location?.coordinates;
    const filterLocation = eventFilter.location;

    if (filterCoords && filterLocation && event.geolocation) {
      distance = getDistanceFromLatLonInKm(
        event.geolocation.coordinates[0],
        event.geolocation.coordinates[1],
        filterCoords[0],
        filterCoords[1]
      );
      return distance;
    } else if (event.geolocation) {
      const savedLocation = localStorage.getItem('location');
      if (savedLocation) {
        const parsedLocation = JSON.parse(savedLocation);
        if (parsedLocation) {
          const {longitude, latitude} = parsedLocation;
          distance = getDistanceFromLatLonInKm(
            event.geolocation.coordinates[0],
            event.geolocation.coordinates[1],
            longitude,
            latitude
          );
          return distance;
        }
      } else if (userInfos.location) {
        distance = getDistanceFromLatLonInKm(
          event.geolocation.coordinates[0],
          event.geolocation.coordinates[1],
          userInfos.location.lat,
          userInfos.location.lng
        );
      }
    }
  }, [event.geolocation, eventFilter.location, userInfos.location]);

  return (
    <Grid
      item
      xs={fullWidth ? 12 : 12}
      sm={fullWidth ? 12 : 6}
      md={fullWidth ? 12 : 6}
      lg={fullWidth ? 12 : 6}
    >
      <Tooltip title={t('open_event')} open={showMainTooltip}>
        <Card
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
          sx={{
            width: '100%',
            cursor: 'pointer'
          }}
          onClick={() => onEventCardClick(event)}
        >
          <CardMediaComponent
            height={cardMediaHeight}
            image={imageUrl || ''}
            altText={sportLabel}
            sport={sportLabel}
            distance={!event.online ? eventDistance : undefined}
            online={event.online}
            showSport={true}
            showOnlineStatus={event.onDemand ? false : true}
            sportNameSize={cardMediaTypography}
            eventCancelled={eventCancelled}
            pastEvent={pastEvent}
          />

          <CardContent
            sx={{
              maxHeight: smallerVariant ? (isUserTrainer ? 85 : 75) : 'none'
            }}
          >
            <Box display="flex" alignItems="center">
              {isUserTrainer && event.company && (
                <ResponsiveTypography smallAllSizes truncateText alwaysTruncate>
                  {getCompanyLabel(event.company, userCompanies || []) ||
                    'company'}
                </ResponsiveTypography>
              )}
            </Box>

            <Box display="flex" alignItems="center">
              <ResponsiveTypography
                {...responsiveTypographyTitleProps}
                truncateText
                alwaysTruncate
              >
                {event.title} {eventCancelled ? ` (${t('cancelled')})` : ''}
                {pastEvent && !eventCancelled ? ` (${t('past_event')})` : ''}
              </ResponsiveTypography>
              {isUserParticipant && (
                <Tooltip
                  title={t('participating_long')}
                  placement="top"
                  onMouseEnter={handleOtherTooltipMouseEnter}
                  onMouseLeave={handleOtherTooltipMouseLeave}
                >
                  <Done color="secondary" sx={{ml: 1}} />
                </Tooltip>
              )}
              {trainerEvent && (
                <Tooltip
                  title={t('trainer_event')}
                  placement="top"
                  onMouseEnter={handleOtherTooltipMouseEnter}
                  onMouseLeave={handleOtherTooltipMouseLeave}
                >
                  <SportsBasketballIcon color="secondary" sx={{ml: 1}} />
                </Tooltip>
              )}
              {isPaid && (
                <Tooltip
                  title={t('paid_event')}
                  placement="top"
                  onMouseEnter={handleOtherTooltipMouseEnter}
                  onMouseLeave={handleOtherTooltipMouseLeave}
                >
                  <EuroIcon color="secondary" sx={{ml: 1}} />
                </Tooltip>
              )}
            </Box>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                minHeight: smallerVariant
                  ? '25px'
                  : isSmScreen && !smallerVariant && !event.onDemand
                  ? '70px'
                  : 'none'
              }}
            >
              {!event.onDemand && (
                <>
                  <ResponsiveTypography {...responsiveTypographyDateProps}>
                    {formattedDate}
                  </ResponsiveTypography>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip
                      title={t('participants_number')}
                      onMouseEnter={handleOtherTooltipMouseEnter}
                      onMouseLeave={handleOtherTooltipMouseLeave}
                    >
                      <Chip
                        size={chipSize}
                        icon={<PersonIcon />}
                        label={getParticipantsLabel()}
                        color="primary"
                      />
                    </Tooltip>
                    {event.recurring.length > 0 && (
                      <Tooltip
                        title={t('recurring_title')}
                        onMouseEnter={handleOtherTooltipMouseEnter}
                        onMouseLeave={handleOtherTooltipMouseLeave}
                      >
                        <Chip
                          size={chipSize}
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Repeat sx={{position: 'relative', top: '1px'}} />
                            </Box>
                          }
                          color="primary"
                        />
                      </Tooltip>
                    )}
                    {event.accessType === 'PRIVATE' && (
                      <Tooltip
                        title={t('private_title')}
                        onMouseEnter={handleOtherTooltipMouseEnter}
                        onMouseLeave={handleOtherTooltipMouseLeave}
                      >
                        <Chip
                          size={chipSize}
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <LockIcon
                                sx={{position: 'relative', top: '1px'}}
                              />
                            </Box>
                          }
                          color="primary"
                        />
                      </Tooltip>
                    )}

                    {unreadEventsCount > 0 && (
                      <Tooltip
                        title={t('unread_events_count')}
                        onMouseEnter={handleOtherTooltipMouseEnter}
                        onMouseLeave={handleOtherTooltipMouseLeave}
                      >
                        <Chip
                          size={chipSize}
                          icon={<NotificationsActiveIcon />}
                          label={unreadEventsCount}
                          color="primary"
                        />
                      </Tooltip>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Tooltip>
    </Grid>
  );
};

export default EventCard;
